import type { BackgroundInput, EffectsPipeline, PipelineError } from 'longpipe'
import type { ProcessorOptions, Track, TrackProcessor } from 'livekit-client'

/** Параметры фона сохраняют совместимость с выбором пользователя в localStorage. */
export type BackgroundOptions = {
  blurRadius?: number
  imagePath?: string
}

const INIT_TIMEOUT_MS = 30_000

/** Обрабатывает камеру в Longpipe; LiveKit управляет публикацией, превью и возвратом исходного трека. */
export default class LongpipeBackgroundProcessor implements TrackProcessor<Track.Kind.Video> {
  name: string

  processedTrack?: MediaStreamTrack

  onFatalError?: (error: unknown) => void

  /** Общий статус скачивания SDK/весов и запуска модели до первого обработанного кадра. */
  onLoadingChange?: (loading: boolean) => void

  private pipeline?: EffectsPipeline

  private initialized = false

  /** Сохраняет эффект до асинхронной загрузки SDK и весов модели.
   * @param options — размытие или изображение фона.
   * @param name — тип эффекта для LiveKit.
   */
  constructor(private options: BackgroundOptions, name = 'background-processor') {
    this.name = name
  }

  /** Проверяет API транспорта; выбор WebGPU или WebGL2 выполняет сам Longpipe. */
  static get isSupported(): boolean {
    return typeof window !== 'undefined' &&
      typeof Worker !== 'undefined' &&
      typeof OffscreenCanvas !== 'undefined' &&
      typeof HTMLCanvasElement.prototype.captureStream === 'function' &&
      typeof createImageBitmap === 'function'
  }

  /** Преобразует прежний радиус размытия в поддерживаемую SDK сигму без смены сохранённых настроек. */
  private getBackground(): BackgroundInput {
    if (this.options.blurRadius !== undefined) {
      return { blur: { sigma: Math.max(0, this.options.blurRadius) } }
    }

    return this.options.imagePath ?? 'none'
  }

  /** Загружает SDK только в браузере и ждёт первый обработанный кадр перед публикацией.
   * @param options — исходный трек камеры от LiveKit.
   */
  async init(options: ProcessorOptions<Track.Kind.Video>): Promise<void> {
    let timeout: ReturnType<typeof setTimeout> | undefined

    this.onLoadingChange?.(true)

    try {
      const { EffectsPipeline: Pipeline } = await import('longpipe')

      // Исходный трек принадлежит LiveKit: SDK получает его без остановки или подмены камеры.
      this.pipeline = new Pipeline(new MediaStream([options.track]), {
        background: this.getBackground(),
        // Явный medium исключает автоподбор; обе точности весов поставляются вместе с приложением.
        preset: {
          model: 'medium',
          dtype: 'f16',
          resolution: { w: 512, h: 320 },
          skipFrames: 1,
        },
        adaptive: false,
        weightsBaseUrl: new URL('/processor/longpipe/v0.0.5/', window.location.origin).href,
        audio: 'drop',
        onError: (error: PipelineError) => {
          console.warn('Ошибка обработки фона Longpipe', error)

          if (!error.recoverable && this.initialized) {
            this.onFatalError?.(error)
          }
        },
      })

      await Promise.race([
        this.pipeline.ready,
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Истекло время запуска Longpipe')), INIT_TIMEOUT_MS)
        }),
      ])

      const [processedTrack] = this.pipeline.stream.getVideoTracks()

      if (!processedTrack) {
        throw new Error('Longpipe не вернул обработанный видеотрек')
      }

      this.processedTrack = processedTrack
      this.initialized = true
    } catch (error) {
      await this.destroy()

      throw error
    } finally {
      clearTimeout(timeout)
      this.onLoadingChange?.(false)
    }
  }

  /** Перезапускает модель при переключении камеры или перезапуске исходного трека LiveKit.
   * @param options — новый исходный трек камеры.
   */
  async restart(options: ProcessorOptions<Track.Kind.Video>): Promise<void> {
    await this.destroy()
    await this.init(options)
  }

  /** Меняет фон без пересоздания модели и сохраняет настройки для следующего перезапуска камеры.
   * @param options — новое размытие или изображение.
   * @param name — тип активного эффекта.
   */
  async update(options: BackgroundOptions, name: string): Promise<void> {
    if (this.name === name && this.options.blurRadius === options.blurRadius &&
      this.options.imagePath === options.imagePath) {
      return
    }

    const previous = this.options

    this.options = options

    try {
      await this.pipeline?.setBackground(this.getBackground())
      this.name = name
    } catch (error) {
      this.options = previous

      throw error
    }
  }

  /** Освобождает worker и выходной трек; исходную камеру продолжает обслуживать LiveKit. */
  async destroy(): Promise<void> {
    const pipeline = this.pipeline

    this.pipeline = undefined
    this.initialized = false
    this.onLoadingChange?.(false)
    this.processedTrack?.stop()
    this.processedTrack = undefined
    pipeline?.destroy()
  }
}
